import { SummaryArticleType, IArticleProxyParser } from './@types';
import AbsctractArticleProxyParser, {
  AbsctractArticleProxyParserContructor,
} from './AbsctractArticleProxyParser';
import type { Occurrence } from '../../../apiConfigs/@types';

class ArticleProxyParser extends AbsctractArticleProxyParser implements IArticleProxyParser {
  constructor(options: AbsctractArticleProxyParserContructor) {
    super(options);
  }

  async getAll(refresh?: boolean | undefined): Promise<SummaryArticleType[]> {
    if (this.cache.hasArticles()) return this.cache.articles;
    // 優先使用 API 查詢
    const articles = await this.getArticlesFromAPI();
    const cache = this.cache;
    cache.summaryMap = articles.reduce(
      (obj, article) => {
        obj[article.title] = article;
        return obj;
      },
      {} as typeof cache.summaryMap,
    );
    return articles;
  }

  async getDetailByTitle(title: string, fallbackUrl?: string | null | undefined) {
    const article = this.cache.summaryMap[title];
    const externalProps = this.cache.externalMap[title];
    const hasArticle = !!article;
    const hasExternal = this.cache.hasExternalKey(title);

    if (hasArticle) {
      if (hasExternal) return { ...article, ...externalProps };

      // 優先使用 API 查詢詳細資訊
      const externalDetail = await this.getExternalDetailFromAPI(title);

      this.cache.externalMap[title] = externalDetail;
      return { ...article, ...externalDetail };
    }

    throw new Error(`failed to get by title: ${title}, get all article first`);
  }

  getArticlesFromHTML(dom: Document) {
    const articleCards = Array.from(dom.querySelectorAll('.sppb-addon-article'));
    const result: SummaryArticleType[] = articleCards.map((cardElem, index) => {
      const title: string = cardElem.querySelector('h3')?.innerText || 'null';
      const content: string =
        cardElem.querySelector('.sppb-article-introtext')?.innerHTML || 'null';
      const imgSrc: string = (cardElem.querySelector('a img') as HTMLImageElement).src || 'null';
      const link: string = cardElem.querySelector('a')?.href || 'null';
      const id: string = `html-${index}`;
      return { id, title, content, imgSrc, link };
    });
    return result;
  }

  private async fetchAllPages(initialUrl: string): Promise<{ data: any[]; included: any[] }> {
    const data: any[] = [];
    const included: any[] = [];
    let nextUrl: string | null = initialUrl;

    while (nextUrl) {
      const res = await fetch(nextUrl);
      const json: any = await res.json();

      if (Array.isArray(json?.data)) data.push(...json.data);
      if (Array.isArray(json?.included)) included.push(...json.included);

      const next: string | null = json?.links?.next?.href ?? null;
      const normalizedNext = next ? next.replace(/^http:\/\//, 'https://') : null;
      nextUrl = normalizedNext && normalizedNext !== nextUrl ? normalizedNext : null;
    }

    return { data, included };
  }

  async getArticlesFromAPI(): Promise<SummaryArticleType[]> {
    try {
      if (this.cmsPath.length > 0) {
        console.warn(
          'ArticleProxyParser: cmsPath filter is no longer supported after switching to SW endpoints',
        );
      }

      type SWFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Point, Occurrence>;
      const responses = await Promise.all(
        this.apiUrls.map((url) =>
          fetch(url)
            .then((r) => r.json() as Promise<SWFeatureCollection>)
            .catch((error) => {
              console.error(`Error fetching from ${url}:`, error);
              return null;
            }),
        ),
      );

      const allFeatures = responses
        .filter((r): r is SWFeatureCollection => r !== null)
        .flatMap((r) => r.features)
        .sort((a, b) => {
          const numA = parseInt(a.properties.occurrence_id.match(/^\d+/)?.[0] || '0');
          const numB = parseInt(b.properties.occurrence_id.match(/^\d+/)?.[0] || '0');
          return numA - numB;
        });

      const result: SummaryArticleType[] = allFeatures.map((feature) => {
        const props = feature.properties;
        const title = props.occurrence_id || 'null';
        let content = props.trail_name || '';
        if (content.length > 34) {
          content = content.substring(0, 34) + '......';
        }
        const [lon, lat] = feature.geometry?.coordinates ?? [];
        return {
          id: props.occurrence_url || title,
          title,
          content,
          imgSrc: props.occurrence_photo_url || 'null',
          link: props.occurrence_url || '',
          latitude: typeof lat === 'number' ? lat : undefined,
          longitude: typeof lon === 'number' ? lon : undefined,
          reporter: props.occurrence || '',
          reportDate: props.report_date || '',
          category: props.listing_category || '',
        };
      });

      return result;
    } catch (error) {
      console.error('Error fetching articles:', error);
      return [];
    }
  }

  // 目前沒用到
  async getExternalDetailFromAPI(title: string): Promise<{
    subtitle: string;
    ref: string;
    content: string;
  }> {
    try {
      // 詳細欄位（body.summary、field_listing_author）SW 端沒帶，
      // 仍需直接打 iriver JSON:API。
      const detailApiUrls = [
        'https://iriver.tw/jsonapi/node/listing',
        'https://iriver.tw/zh-hant/jsonapi/node/advance_listing/',
      ];
      const responses = await Promise.all(
        detailApiUrls.map((url) =>
          this.fetchAllPages(url).catch((error) => {
            console.error(`Error fetching from ${url}:`, error);
            return null;
          }),
        ),
      );

      // 合併所有回應的資料並尋找符合的項目
      const allData = responses
        .filter((response) => response !== null)
        .flatMap((response) => response!.data);

      const item = allData.find((element: any) => element.attributes.title === title);

      if (item) {
        const subtitle: string = item.attributes.title || 'null';
        const ref: string = `撰稿者：${item.attributes.field_listing_author}` || 'null';
        const content: string = item.attributes.body?.summary || 'null';

        return { subtitle, ref, content };
      }

      return { subtitle: 'null', ref: 'null', content: 'null' };
    } catch (error) {
      console.error('Error fetching external detail:', error);
      return { subtitle: 'null', ref: 'null', content: 'null' };
    }
  }

  getExternalDetailFromHTML(dom: Document) {
    const newContent = (dom.querySelector('.sppb-addon-content p.d-h3') as HTMLHeadingElement)
      ?.innerText;
    const subtitle = (dom.querySelector('.entry-header .tags') as HTMLDivElement)?.innerText;
    const ref = (dom.querySelector('.sppb-addon-content p:nth-child(4) span') as HTMLSpanElement)
      ?.innerText;
    return { subtitle, ref, content: newContent };
  }
}

export default ArticleProxyParser;
